// src/components/test/StoragePermissionTester.tsx
import React, { useState } from "react";
import { supabase } from "@/lib/supabase";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { v4 as uuidv4 } from "uuid";

export const StoragePermissionTester = () => {
  const [testResults, setTestResults] = useState<any[]>([]);
  const [testing, setTesting] = useState(false);

  const runTests = async () => {
    setTesting(true);
    const results = [];

    try {
      // 1. Test authentication
      const {
        data: { user },
      } = await supabase.auth.getUser();
      results.push({
        name: "Authentication check",
        success: !!user,
        message: user ? `Authenticated as ${user.email}` : "Not authenticated",
      });

      if (!user) {
        setTestResults(results);
        setTesting(false);
        return;
      }

      // 2. Test bucket existence
      const buckets = [
        "original-photos",
        "watermarked-photos",
        "temp-originals",
      ];
      for (const bucket of buckets) {
        try {
          const { data, error } = await supabase.storage
            .from(bucket)
            .list("", { limit: 1 });
          results.push({
            name: `Bucket existence: ${bucket}`,
            success: !error,
            message: error
              ? `Error: ${error.message}`
              : `Success: Bucket exists`,
          });
        } catch (e) {
          results.push({
            name: `Bucket existence: ${bucket}`,
            success: false,
            message: `Exception: ${e.message}`,
          });
        }
      }

      // 3. Test file upload
      const testFile = new Blob(["test"], { type: "text/plain" });
      const fileName = `test-${uuidv4()}.txt`;

      try {
        // Try temp bucket first
        const { data: uploadData, error: uploadError } = await supabase.storage
          .from("temp-originals")
          .upload(fileName, testFile);

        results.push({
          name: "Upload to temp-originals",
          success: !uploadError,
          message: uploadError
            ? `Error: ${uploadError.message}`
            : "Success: Uploaded test file",
        });

        // Clean up
        if (!uploadError) {
          await supabase.storage.from("temp-originals").remove([fileName]);
          results.push({
            name: "Delete from temp-originals",
            success: true,
            message: "Success: Deleted test file",
          });
        }
      } catch (e) {
        results.push({
          name: "Upload to temp-originals",
          success: false,
          message: `Exception: ${e.message}`,
        });
      }

      // 4. Test edge function access
      try {
        const { data, error } = await supabase.functions.invoke("watermark", {
          body: { testMode: true },
        });

        results.push({
          name: "Edge function access",
          success: !error,
          message: error
            ? `Error: ${error.message}`
            : "Success: Edge function responded",
        });
      } catch (e) {
        results.push({
          name: "Edge function access",
          success: false,
          message: `Exception: ${e.message}`,
        });
      }
    } catch (error) {
      results.push({
        name: "General test failure",
        success: false,
        message: `Error: ${error.message}`,
      });
    } finally {
      setTestResults(results);
      setTesting(false);
    }
  };

  return (
    <Card className="w-full">
      <CardHeader>
        <CardTitle>Storage Permission Tester</CardTitle>
      </CardHeader>
      <CardContent>
        <Button onClick={runTests} disabled={testing} className="mb-4">
          {testing ? "Testing..." : "Run Permission Tests"}
        </Button>

        {testResults.length > 0 && (
          <div className="space-y-2">
            {testResults.map((result, index) => (
              <div
                key={index}
                className={`p-3 rounded-lg ${
                  result.success
                    ? "bg-green-100 text-green-800"
                    : "bg-red-100 text-red-800"
                }`}
              >
                <div className="font-medium">{result.name}</div>
                <div className="text-sm">{result.message}</div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
